<template>
  <div class="w-full">
    <!-- Header Image Section (for IMAGE header templates) -->
    <div v-if="hasImageHeader" class="header-image-section">
      <div class="header-image-label">
        <span class="text-sm font-semibold">🖼️ Görsel Header</span>
        <span class="required-badge">Zorunlu</span>
      </div>

      <!-- Drag & Drop Zone -->
      <div
        class="dropzone"
        :class="{ 'dropzone--active': isDragging, 'dropzone--has-image': headerImageUrl }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="triggerFileInput"
      >
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleFileSelect"
        />

        <!-- Upload Progress -->
        <div v-if="isUploading" class="dropzone-content">
          <div class="upload-spinner"></div>
          <span class="text-sm text-slate-500">Yükleniyor...</span>
        </div>

        <!-- Image Preview -->
        <div v-else-if="headerImageUrl" class="dropzone-preview">
          <img
            :src="headerImageUrl"
            alt="Header preview"
            class="preview-image"
            @error="headerImageUrl = ''"
          />
          <button
            type="button"
            class="remove-image-btn"
            @click.stop="removeImage"
          >
            ✕
          </button>
        </div>

        <!-- Empty State -->
        <div v-else class="dropzone-content">
          <span class="dropzone-icon">📁</span>
          <span class="text-sm text-slate-500">
            Görsel sürükle-bırak veya tıkla
          </span>
          <span class="text-xs text-slate-400">PNG, JPG, JPEG (max 5MB)</span>
        </div>
      </div>

      <!-- URL Input (alternative) -->
      <div class="url-input-section">
        <span class="text-xs text-slate-400">veya URL girin:</span>
        <input
          v-model="headerImageUrl"
          type="url"
          class="header-image-input"
          placeholder="https://example.com/image.jpg"
        />
      </div>

      <!-- Actions -->
      <div class="header-image-actions">
        <span v-if="headerImageError" class="error-text">
          Geçerli bir görsel URL'si giriniz
        </span>
        <button
          v-if="headerImageUrl && !headerImageError"
          type="button"
          class="save-default-btn"
          @click="saveDefaultImage"
        >
          💾 Varsayılan olarak kaydet
        </button>
      </div>
    </div>

    <!-- Video/Document Header Info -->
    <div v-else-if="hasVideoHeader" class="media-header-warning">
      <span>🎥 Bu şablon video header içeriyor - video URL'si gerekli</span>
    </div>
    <div v-else-if="hasDocumentHeader" class="media-header-warning">
      <span>📄 Bu şablon doküman header içeriyor - doküman URL'si gerekli</span>
    </div>

    <textarea
      v-model="processedString"
      rows="4"
      readonly
      class="template-input"
    />
    <div v-if="variables" class="template__variables-container">
      <p class="variables-label">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
      </p>
      <div
        v-for="(variable, key) in processedParams"
        :key="key"
        class="template__variable-item"
      >
        <span class="variable-label">
          {{ key }}
        </span>
        <!-- Variable attribute badges - SMS-style clickable selection -->
        <div class="variable-badges-container">
          <button
            v-for="attr in contactAttributes"
            :key="attr.key"
            type="button"
            class="variable-badge"
            :class="{ 'variable-badge--selected': processedParams[key] === attr.value }"
            @click="selectAttribute(key, attr.value)"
          >
            {{ attr.label }}
          </button>
        </div>
        <!-- Selected value display -->
        <span v-if="processedParams[key]" class="selected-value">
          {{ processedParams[key] }}
        </span>
      </div>
      <p v-if="hasValidationError" class="error">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>
    <footer>
      <woot-button variant="smooth" @click="$emit('resetTemplate')">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
      </woot-button>
      <woot-button type="button" :disabled="!isFormValid" @click="sendMessage">
        {{ $t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL') }}
      </woot-button>
    </footer>
  </div>
</template>

<script>
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { useAlert } from 'dashboard/composables';

export default {
  props: {
    template: {
      type: Object,
      default: () => {},
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    defaultHeaderImageUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      processedParams: {},
      headerImageUrl: '',
      formTouched: false,
      isDragging: false,
      isUploading: false,
      lastTemplateName: '',
      // Contact attribute options for badge selection (SMS-style)
      contactAttributes: [
        { key: 'name', label: 'İsim', value: '{{contact.name}}' },
        { key: 'first_name', label: 'Ad', value: '{{contact.first_name}}' },
        { key: 'last_name', label: 'Soyad', value: '{{contact.last_name}}' },
        { key: 'email', label: 'E-posta', value: '{{contact.email}}' },
        { key: 'phone', label: 'Telefon', value: '{{contact.phone_number}}' },
        { key: 'company', label: 'Okul/Şirket', value: '{{contact.company_name}}' },
        { key: 'city', label: 'Şehir', value: '{{contact.city}}' },
      ],
    };
  },
  computed: {
    variables() {
      const variables = this.templateString.match(/{{([^}]+)}}/g);
      return variables;
    },
    templateString() {
      return this.template.components.find(
        component => component.type === 'BODY'
      ).text;
    },
    processedString() {
      return this.templateString.replace(/{{([^}]+)}}/g, (match, variable) => {
        const variableKey = this.processVariable(variable);
        return this.processedParams[variableKey] || `{{${variable}}}`;
      });
    },
    // Header format detection
    headerComponent() {
      return this.template.components?.find(c => c.type === 'HEADER');
    },
    headerFormat() {
      return this.headerComponent?.format?.toUpperCase();
    },
    hasImageHeader() {
      return this.headerFormat === 'IMAGE';
    },
    hasVideoHeader() {
      return this.headerFormat === 'VIDEO';
    },
    hasDocumentHeader() {
      return this.headerFormat === 'DOCUMENT';
    },
    hasMediaHeader() {
      return this.hasImageHeader || this.hasVideoHeader || this.hasDocumentHeader;
    },
    // Validation for header image
    headerImageError() {
      if (!this.hasImageHeader) return false;
      if (!this.headerImageUrl) return false;
      try {
        new URL(this.headerImageUrl);
        return false;
      } catch {
        return true;
      }
    },
    // Check if all variables are filled
    allVariablesFilled() {
      if (!this.variables || this.variables.length === 0) return true;
      const keys = Object.keys(this.processedParams);
      return keys.every(key => this.processedParams[key]);
    },
    // Form validation state
    hasValidationError() {
      return this.formTouched && !this.allVariablesFilled;
    },
    isFormValid() {
      // Check if variables are filled
      const variablesValid = this.allVariablesFilled;
      // Check if header image is valid (if required)
      const headerValid = !this.hasImageHeader || (this.headerImageUrl && !this.headerImageError);
      return variablesValid && headerValid;
    },
  },
  watch: {
    defaultHeaderImageUrl: {
      immediate: true,
      handler(newUrl) {
        // Always sync with default URL when it changes (after save)
        if (newUrl) {
          this.headerImageUrl = newUrl;
        }
      },
    },
    template: {
      immediate: true,
      handler(newTemplate) {
        // Only reset when template NAME actually changes (not just object reference)
        const currentName = newTemplate?.name || '';
        if (this.lastTemplateName !== currentName) {
          this.headerImageUrl = this.defaultHeaderImageUrl || '';
          this.lastTemplateName = currentName;
        }
      },
    },
  },
  mounted() {
    this.generateVariables();
    // Initialize header image URL from default
    if (this.defaultHeaderImageUrl) {
      this.headerImageUrl = this.defaultHeaderImageUrl;
    }
  },
  methods: {
    sendMessage() {
      this.formTouched = true;
      if (!this.isFormValid) return;
      const payload = {
        message: this.processedString,
        templateParams: {
          name: this.template.name,
          category: this.template.category,
          language: this.template.language,
          namespace: this.template.namespace,
          processed_params: this.processedParams,
        },
      };
      // Add header image URL if template has IMAGE header
      if (this.hasImageHeader && this.headerImageUrl) {
        payload.templateParams.header_image_url = this.headerImageUrl;
      }
      this.$emit('sendMessage', payload);
    },
    processVariable(str) {
      return str.replace(/{{|}}/g, '');
    },
    generateVariables() {
      const matchedVariables = this.templateString.match(/{{([^}]+)}}/g);
      if (!matchedVariables) return;

      const variables = matchedVariables.map(i => this.processVariable(i));
      this.processedParams = variables.reduce((acc, variable) => {
        acc[variable] = '';
        return acc;
      }, {});
    },
    // Handle badge click to select an attribute for a variable
    selectAttribute(variableKey, attributeValue) {
      this.$set(this.processedParams, variableKey, attributeValue);
    },
    // Save current image as default for this template
    saveDefaultImage() {
      if (this.headerImageUrl && !this.headerImageError) {
        this.$emit('saveDefaultImage', {
          templateName: this.template.name,
          imageUrl: this.headerImageUrl,
        });
      }
    },
    // Drag & Drop handlers
    triggerFileInput() {
      if (!this.isUploading) {
        this.$refs.fileInput.click();
      }
    },
    handleDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadImage(files[0]);
      }
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.uploadImage(files[0]);
      }
      // Reset input so same file can be selected again
      event.target.value = '';
    },
    async uploadImage(file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        useAlert('Lütfen bir görsel dosyası seçin');
        return;
      }
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        useAlert('Görsel boyutu 5MB\'dan küçük olmalıdır');
        return;
      }

      this.isUploading = true;
      try {
        const { fileUrl } = await uploadFile(file);
        this.headerImageUrl = fileUrl;
        useAlert('Görsel yüklendi');
      } catch (error) {
        useAlert(error.message || 'Görsel yüklenirken hata oluştu');
      } finally {
        this.isUploading = false;
      }
    },
    removeImage() {
      this.headerImageUrl = '';
    },
  },
};
</script>

<style scoped lang="scss">
/* Header Image Section Styles */
.header-image-section {
  @apply mb-4 p-3 rounded-lg bg-slate-25 dark:bg-slate-800 border border-slate-100 dark:border-slate-700;
}

.header-image-label {
  @apply flex items-center gap-2 mb-2;
}

.required-badge {
  @apply text-xs px-2 py-0.5 rounded;
  background-color: #fef3c7;
  color: #92400e;
}

/* Dropzone Styles */
.dropzone {
  @apply relative flex flex-col items-center justify-center p-4 mb-2 rounded-lg border-2 border-dashed cursor-pointer transition-all;
  @apply border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900;
  min-height: 120px;
}

.dropzone:hover {
  @apply border-woot-400 bg-woot-25;
}

.dropzone--active {
  @apply border-woot-500 bg-woot-50;
}

.dropzone--has-image {
  @apply p-2;
}

.dropzone-content {
  @apply flex flex-col items-center gap-1;
}

.dropzone-icon {
  @apply text-3xl mb-1;
}

.dropzone-preview {
  @apply relative w-full flex justify-center;
}

.dropzone-preview .preview-image {
  @apply max-h-32 rounded;
  object-fit: contain;
}

.remove-image-btn {
  @apply absolute top-0 right-0 w-6 h-6 flex items-center justify-center rounded-full text-xs;
  @apply bg-slate-700 text-white;
}

.remove-image-btn:hover {
  background-color: #ef4444;
}

.upload-spinner {
  @apply w-6 h-6 border-2 border-woot-500 rounded-full;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.hidden {
  display: none;
}

.url-input-section {
  @apply flex items-center gap-2 mb-2;
}

.header-image-input {
  @apply flex-1 px-3 py-1.5 text-sm border rounded;
  @apply bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-600;
  outline: none;
}

.header-image-input:focus {
  @apply border-woot-500;
}

.header-image-actions {
  @apply flex items-center justify-between;
}

.error-text {
  @apply text-xs;
  color: #ef4444;
}

.save-default-btn {
  @apply text-xs text-woot-600 flex items-center;
  gap: 0.25rem;
}

.save-default-btn:hover {
  @apply text-woot-700;
  text-decoration: underline;
}

.media-header-warning {
  @apply mb-4 p-3 rounded text-sm;
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  color: #92400e;
}

.template__variables-container {
  @apply p-2.5;
}

.variables-label {
  @apply text-sm font-semibold mb-2.5;
}

.template__variable-item {
  @apply items-center flex mb-2.5;

  .label {
    @apply text-xs;
  }

  .variable-input {
    @apply flex-1 text-sm ml-2.5;
  }

  .variable-label {
    @apply bg-slate-75 dark:bg-slate-700 text-slate-700 dark:text-slate-100 inline-block rounded-md text-xs py-2.5 px-6;
  }
}

footer {
  @apply flex justify-end;

  button {
    @apply ml-2.5;
  }
}

.error {
  @apply rounded-md p-2.5 text-center;
  background-color: #fee2e2;
  color: #991b1b;
}

.template-input {
  @apply bg-slate-25 dark:bg-slate-900 text-slate-700 dark:text-slate-100;
}

/* Variable badge styles - SMS-style clickable tags */
.variable-badges-container {
  @apply flex flex-wrap ml-2.5 flex-1;
  gap: 0.375rem;
}

.variable-badge {
  @apply inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer;
  @apply bg-woot-50 text-woot-700 border border-woot-200;
  transition: all 0.2s;
}

.variable-badge:hover {
  @apply bg-woot-100 border-woot-300;
}

.variable-badge--selected {
  @apply bg-woot-500 text-white border-woot-500;
}

.variable-badge--selected:hover {
  @apply bg-woot-600 border-woot-600;
}

.selected-value {
  @apply ml-2 text-xs text-slate-500 dark:text-slate-400;
  font-style: italic;
}
</style>
